import React, {useState} from "react"
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import CCORoofline from "../../fundamentals/CCORoofline";
import {textDecorationStyle} from "../../fundamentals/CCOLinkText";
import CCODownloadButton from "../../fundamentals/CCODownloadButton";
import {useTheme} from '@mui/material/styles';
import {isTargetCMDownload} from "./TeaserCard";
import TeaserCardCallToAction, {CallToActionType} from "./TeaserCardCallToAction";
import CCOPicture from "../../fundamentals/CCOPicture";
import useWindowSize from "../../../utility/useWindowSize";
import {getLengthLimit} from "../../../utility/lengthLimits";
import {usePreviewLimitCheck} from "../../preview/usePreviewLimitCheck";
import {stripTreeText} from "../../../utility/componentUtil";
import CCOAiGeneratedImageLabel, {
    AiGeneratedImageLabel,
    useAiGeneratedImageLabel
} from "../../fundamentals/CCOAiGeneratedImageLabel";

interface DescriptionLimitMeta {
    value?: number;
    source?: 'default' | string;
    ownerId?: string;
}

interface Props {
    item: any
    pill?: string
    horizontal?: boolean
    headlineType?: string
    descriptionLimit?: DescriptionLimitMeta
    callToActionType?: CallToActionType
    callToActionCustomText?: string
    showHorizontalPictureOnRight?: boolean,
    hero?: boolean
    insideCardWithLink?: boolean
    hideRoofline?: boolean
    hasPriority?: boolean
    showAiGeneratedImageLabel?: boolean
}

export const DESCRIPTION_CHAR_LIMIT = getLengthLimit('C020TeaserCard.description');
export const HEADER_CHAR_LIMIT = getLengthLimit('C020TeaserCard.header');
export const ROOF_LINE_CHAR_LIMIT = getLengthLimit('C020TeaserCard.roofline');

export const cardStyle = (props: Props, color: string) => (
    {
        display: 'grid',
        gridTemplateColumns: props.horizontal ? '1fr 1fr' : 'unset',
        gridTemplateRows: props.horizontal ? 'unset' : 'max-content max-content',
        height: '100%!important',
        width: '100%!important',
        '&:hover': {
            '& h3, h5': {
                color: !props.item.teaserTarget ? 'inherit' : color,
                transition: !props.item.teaserTarget ? 'unset' : 'color 1s ease-out',
            },
            '& div > p': !props.item.teaserTarget ? 'unset' : textDecorationStyle,
        },
    });

const pillCardStyle = (props: Props, color: string) => (
    {
        ...cardStyle(props, color), ...{background: 'var(--background-grey)'}
    });

function TeaserCardContent(props: Props) {
    const theme = useTheme();
    const color = theme.palette.primary.main;
    const [isCardHovered, setIsCardHovered] = useState(false);
    const handleMouseOver = () => setIsCardHovered(true);
    const handleMouseOut = () => setIsCardHovered(false);

    const aiLabel = useAiGeneratedImageLabel(props.showAiGeneratedImageLabel ? props.item?.picture : undefined);

    const effectiveDescriptionLimit = props.descriptionLimit?.value ?? DESCRIPTION_CHAR_LIMIT;
    const teaserTitle = props.item?.teaserTitle || '';
    const rooflineText = props.item?.headline || '';
    const teaserTextPlain = props.item?.teaserText?.textAsTree ? stripTreeText(props.item.teaserText.textAsTree) : '';

    usePreviewLimitCheck({
        id: props.item?.id,
        component: 'C020TeaserCarousel.TeaserCardContent',
        field: 'teaserTitle',
        value: teaserTitle,
        limit: HEADER_CHAR_LIMIT,
        source: 'default',
    });

    usePreviewLimitCheck({
        id: props.item?.id,
        component: 'C020TeaserCarousel.TeaserCardContent',
        field: 'headline',
        value: rooflineText,
        limit: ROOF_LINE_CHAR_LIMIT,
        source: 'default',
    });

    usePreviewLimitCheck({
        id: props.item?.id,
        component: 'C020TeaserCarousel.TeaserCardContent',
        field: 'teaserText',
        value: teaserTextPlain,
        limit: effectiveDescriptionLimit,
        source: props.descriptionLimit?.source ?? 'default',
        settingOwnerId: props.descriptionLimit?.ownerId,
    });

    return <Box id={props.item.id}
                sx={props.pill ? pillCardStyle(props, color) : cardStyle(props, color)}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}>
        {renderContent(props, color, isCardHovered, aiLabel)}
    </Box>
}

function renderContent(props: Props, color: string, isCardHovered: boolean, aiLabel?: AiGeneratedImageLabel) {
    return props.horizontal && props.showHorizontalPictureOnRight ?
        <React.Fragment>
            {renderText(props, color, isCardHovered)}
            {renderPicture(props, aiLabel)}
        </React.Fragment> :
        <React.Fragment>
            {renderPicture(props, aiLabel)}
            {renderText(props, color, isCardHovered)}
        </React.Fragment>
}

function renderPicture(props: Props, aiLabel?: AiGeneratedImageLabel) {
    if (!props.item.picture) return null;
    return <Box
        sx={{
            alignSelf: props.showAiGeneratedImageLabel ? 'start' : undefined,
            width: "585",
            height: "329",
            overflow: 'hidden',
            position: 'relative',
            "img": {width: '100%!important', height: '100%!important'}
        }}>
        <CCOPicture name={props.item.picture?.name}
                    picture={props.item.picture}
                    orientation={"landscape_ratio16x9"} width={"585"}
                    height={"329"} hasPriority={props.hasPriority}/>
        {aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='tile'/>}
    </Box>;
}

function renderText(props: Props, color: string, isCardHovered: boolean) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {greaterThanWindowMedium, lowerThanWindowMedium, lowerThanWindowSmall} = useWindowSize();

    const CARD_CONTENT_STYLE = {
        'div:last-child': {
            marginBottom: '0 !important',
        },
        'span > div:last-child': {
            marginBottom: '24px !important',
        },
        paddingLeft: (props.hero && (greaterThanWindowMedium || lowerThanWindowSmall)) ||
        lowerThanWindowMedium ? '16px' : '24px',
        paddingTop: (props.hero && (greaterThanWindowMedium || lowerThanWindowSmall)) ||
        lowerThanWindowMedium ? '16px' : '24px',
        height: '100%!important',
        width: '100%!important',
    }
    const PILL_CONTENT_STYLE = {...CARD_CONTENT_STYLE, ...{gridTemplateRows: 'max-content 2fr max-content'}}


    return <CardContent sx={props.pill ? PILL_CONTENT_STYLE : CARD_CONTENT_STYLE}>
        {renderPillBox(props, color)}
        {renderTitle(props)}
        {renderRichText(props)}
        {renderDownloadButton(props, isCardHovered)}
        <TeaserCardCallToAction item={props.item}
                                callToActionType={props.callToActionType}
                                callToActionCustomText={props.callToActionCustomText}
                                insideCardWithLink={props.insideCardWithLink}
        />
    </CardContent>
}

function renderPillBox(props: Props, color: string) {
    return props.pill &&
        <Box style={{marginBottom: '10px'}}>
            <Chip label={props.pill} style={{
                backgroundColor: color,
                color: 'white'
            }} size='small' variant='outlined'/>
        </Box>
}

function renderTitle(props: Props) {
    return <Box>
        {!props.hideRoofline && (
            <CCORoofline metadataId={props.item.id} roofLine={props.item.headline}
                         roofLineCharLimit={ROOF_LINE_CHAR_LIMIT}
                         propertyName='properties.headline'/>
        )}
        <CCOHeadline type={props.headlineType ? props.headlineType : 'h5'}
                     propertyName={'properties.teaserTitle'}
                     metadataId={props.item.id} headline={props.item.teaserTitle}
                     limit={HEADER_CHAR_LIMIT} justify='left'/>
    </Box>;
}

function renderRichText(props: Props) {
    const descriptionCharLimit = props.descriptionLimit?.value ?? DESCRIPTION_CHAR_LIMIT;
    return <CCORichText metadataId={props.item.id} textAsTree={props.item.teaserText?.textAsTree}
                        propertyName='properties.teaserText'
                        charLimit={descriptionCharLimit}/>;
}

function renderDownloadButton(props: Props, isCardHovered: boolean) {
    return isTargetCMDownload(props.item) &&
        <Box sx={{display: 'flex', justifyContent: 'left'}}>
            <CCODownloadButton download={[props.item.teaserTarget]}
                               downloadTitle={props.item.teaserTarget.teaserTitle}
                               noLink={true}
                               isHovered={isCardHovered}
                               metadataId={props.item.id}
                               propertyName='properties.teaserTarget'/>
        </Box>;
}

export default TeaserCardContent
